import React, { useState, useEffect, useRef } from "react";
import { FiGift as Gift, FiCopy as Copy, FiShare2 as Share2, FiCheck as Check, FiZap as Sparkles } from "react-icons/fi";

/**
 * Mental Swasthya — Session Complete
 * Built with sage / mint palette, aura breathing animation on mount,
 * session timeline, segmented referral reward track, and next reward focus card.
 */

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Work+Sans:wght@400;500;600;700;800&display=swap');`;

const STEPS = [
  { key: "intention", label: "Intention", icon: "📄" },
  { key: "breathing", label: "Breathing", icon: "🌬️" },
  { key: "meditation1", label: "Meditation", icon: "🧘" },
  { key: "meditation2", label: "Meditation", icon: "🧘" },
  { key: "affirmation", label: "Affirmation", icon: "✨" },
];

export default function CompletionScreen({
  onDone,
  onReset,
  stepStatus = {
    intention: "Completed",
    breathing: "4 min",
    meditation1: "10 min",
    meditation2: "5 min",
    affirmation: "Completed",
  },
  streakDays = 5,
  referralCount = 2,
  refLink = "mentalswasthya.com/ref/MS-REF2026",
  apiMessage = ""
}) {
  const [localApiMessage, setLocalApiMessage] = useState(apiMessage);
  const [mounted, setMounted] = useState(false);
  const msgTimer = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 30);
    return () => clearTimeout(t);
  }, []);

  const flash = (text) => {
    setLocalApiMessage(text);
    clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setLocalApiMessage(""), 2500);
  };

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(`https://${refLink}`);
      flash("Link copied — send it to a friend.");
    } catch {
      flash("Couldn't copy automatically — select the link to copy it.");
    }
  };

  const shareReferralLink = async () => {
    setPausedAutoClose(true);
    const inviteMsg = `Hey, \nI came across the "Mental Swasthya" platform, that offers a variety of wellness tools which truly helps you to find peace of mind. \n\nIt is simple, helpful, and perfect for daily relaxation.\n\nI hope you enjoy using it!\n\nClick the below link to register\nhttps://${refLink}`;

    if (navigator.share) {
      try {
        // The link is already part of the message text; passing `url` too would duplicate it.
        await navigator.share({ title: "Mental Swasthya", text: inviteMsg });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return; // user closed the share sheet
        /* share unsupported / failed — fall through to copy */
      }
    }
    try {
      await navigator.clipboard.writeText(inviteMsg);
      flash("Invite message copied — paste it to a friend.");
    } catch {
      flash("Couldn't share automatically — copy the link above.");
    }
  };

  const handleShare = () => shareReferralLink();
  const handleDone = () => (onDone ? onDone() : flash("Returning to dashboard…"));
  const handleTomorrow = () => (onReset ? onReset() : flash("See you tomorrow."));

  const [pausedAutoClose, setPausedAutoClose] = useState(false);

  useEffect(() => {
    if (pausedAutoClose) return;
    const autoCloseTimer = setTimeout(() => {
      if (onDone) onDone();
    }, 15000);
    return () => clearTimeout(autoCloseTimer);
  }, [onDone, pausedAutoClose]);

  const nextRewardAt = referralCount >= 3 ? null : referralCount + 1;

  return (
    <div onTouchStart={() => setPausedAutoClose(true)} className="fixed inset-0 z-50 h-dvh flex items-center justify-center bg-gradient-to-br from-[#f0f6f0] via-[#f7fbf7] to-[#eef6f6] px-3 sm:px-4 md:px-6 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:py-4 md:py-6 overflow-y-auto overscroll-contain">
      <style>{`
        ${FONT_IMPORT}
        .ms-serif { font-family: 'Fraunces', serif; }
        .ms-sans { font-family: 'Work Sans', sans-serif; }

        @keyframes ms-rise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ms-aura {
          0%   { opacity: 0; transform: scale(0.85); }
          55%  { opacity: 0.9; transform: scale(1.04); }
          100% { opacity: 0.55; transform: scale(1); }
        }
        @keyframes ms-fill {
          from { width: 0%; }
        }

        /* ── Share Attention Animations ──────────────────────── */
        @keyframes ms-share-pulse-glow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(125, 150, 103, 0.6), 0 4px 12px rgba(30, 48, 25, 0.15);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(125, 150, 103, 0), 0 8px 22px rgba(125, 150, 103, 0.4);
            transform: scale(1.045);
          }
        }

        @keyframes ms-share-soft-bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-4px);
          }
          60% {
            transform: translateY(-2px);
          }
        }

        @keyframes ms-glowing-ring {
          0%, 100% {
            border-color: #8fa779;
            box-shadow: 0 0 0 1px rgba(125, 150, 103, 0.35), 0 2px 8px rgba(125, 150, 103, 0.1);
          }
          50% {
            border-color: #4a6b47;
            box-shadow: 0 0 0 3px rgba(74, 107, 71, 0.4), 0 4px 16px rgba(74, 107, 71, 0.25);
          }
        }

        .ms-mount { animation: ms-rise 0.6s cubic-bezier(0.16,1,0.3,1) both; }
        .ms-aura-anim { animation: ms-aura 1.8s cubic-bezier(0.16,1,0.3,1) both; }
        .ms-track-fill { animation: ms-fill 1s cubic-bezier(0.16,1,0.3,1) 0.3s both; }

        .ms-share-btn-attention {
          animation: ms-share-pulse-glow 2.2s ease-in-out infinite, ms-share-soft-bounce 4.4s ease-in-out infinite;
        }

        .ms-share-nudge-glow {
          animation: ms-glowing-ring 2.8s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .ms-mount, .ms-aura-anim, .ms-track-fill, .ms-share-btn-attention, .ms-share-nudge-glow { animation: none !important; }
        }
      `}</style>

      <div
        className={`ms-sans w-full max-w-2xl lg:max-w-4xl rounded-[24px] sm:rounded-[28px] bg-white shadow-[0_20px_60px_-15px_rgba(30,48,25,0.18)] border border-[#e7f0e2] my-auto overflow-hidden ${
          mounted ? "ms-mount" : "opacity-0"
        }`}
      >
        {/* ── Hero ───────────────────────────────────────────── */}
        <div className="relative px-4 sm:px-6 md:px-10 pt-7 sm:pt-10 pb-6 sm:pb-8 text-center bg-gradient-to-b from-[#f4faf1] to-white">
          <div className="relative mx-auto w-32 h-[6.5rem] sm:w-40 sm:h-32">
            {mounted && (
              <div className="ms-aura-anim absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(184,215,205,0.55)_0%,rgba(184,215,205,0)_70%)]" />
            )}
            <svg viewBox="0 0 200 160" className="relative w-full h-full overflow-visible">
              <defs>
                <linearGradient id="ms-leaf-1" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#c3dec5" />
                  <stop offset="100%" stopColor="#a9cdb7" />
                </linearGradient>
                <linearGradient id="ms-leaf-2" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#b9d7cd" />
                  <stop offset="100%" stopColor="#96c0b8" />
                </linearGradient>
              </defs>
              <circle cx="75" cy="50" r="22" fill="url(#ms-leaf-1)" opacity="0.85" />
              <circle cx="125" cy="50" r="22" fill="url(#ms-leaf-1)" opacity="0.85" />
              <circle cx="100" cy="35" r="25" fill="url(#ms-leaf-2)" opacity="0.9" />
              <circle cx="60" cy="70" r="18" fill="#dceade" opacity="0.75" />
              <circle cx="140" cy="70" r="18" fill="#dbe7e7" opacity="0.75" />
              <path
                d="M96 95 C96 90 94 75 92 65 C92 65 80 50 78 48 M98 68 C98 68 112 52 115 50 M104 95 C104 90 106 75 108 65"
                stroke="#5c6f52" strokeWidth="2" strokeLinecap="round" fill="none"
              />
              <path d="M96 95 L96 110 L104 110 L104 95 Z" fill="#5c6f52" />
              <circle cx="100" cy="95" r="18" stroke="#9db287" strokeWidth="1" fill="#ffffff" strokeDasharray="3 3" />
              <circle cx="100" cy="85" r="4.5" fill="#4b5563" />
              <path d="M100 89.5 L100 99" stroke="#4b5563" strokeWidth="2.5" strokeLinecap="round" />
              <path d="M88 103 C92 98 108 98 112 103" stroke="#4b5563" strokeWidth="3" strokeLinecap="round" fill="none" />
              <path d="M94 92 C90 95 90 101 94 101 M106 92 C110 95 110 101 106 101" stroke="#4b5563" strokeWidth="1.8" strokeLinecap="round" fill="none" />
            </svg>
          </div>

          <h1 className="ms-serif text-[2rem] sm:text-[2.3rem] leading-none font-medium text-[#1e3019] mt-3 sm:mt-4">
            Well done.
          </h1>
          <p className="text-[13.5px] text-[#5c6f52] mt-3 max-w-sm mx-auto leading-relaxed">
            You showed up for your mind today. Take a moment — notice the stillness you just made room for.
          </p>
        </div>

        <div className="px-4 sm:px-6 md:px-10 pb-6 sm:pb-8">
          {/* ── Session timeline ── */}
          <div className="rounded-2xl bg-[#f7faf5] border border-[#e6efe0] px-2 sm:px-4 py-4 sm:py-5 -mt-1">
            <div className="relative grid grid-cols-5 gap-0.5 sm:gap-1">
              <div className="absolute left-[10%] right-[10%] top-[18px] h-px bg-[#cfe0c6]" />
              {STEPS.map((s) => (
                <div key={s.key} className="relative flex flex-col items-center text-center">
                  <div className="w-9 h-9 rounded-full bg-white border border-[#cfe0c6] flex items-center justify-center text-sm shadow-sm">
                    {s.icon}
                  </div>
                  <span className="text-[9.5px] font-semibold text-[#3a4d31] mt-2 leading-tight break-words max-w-full">
                    {s.label}
                  </span>
                  <span className="text-[9px] text-[#7d9667] mt-0.5">{stepStatus[s.key]}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 mt-5 pt-4 border-t border-[#e6efe0] text-center">
              <span className="text-xs">🔥</span>
              <span className="text-xs font-semibold text-[#8a5a1f]">{streakDays}-day streak</span>
              <span className="text-xs text-[#8a8f7f]">— your longest run yet</span>
            </div>
          </div>

          {/* ── Share nudge ───────────────────────────────── */}
          <button
            type="button"
            onClick={handleShare}
            onMouseEnter={() => setPausedAutoClose(true)}
            className="w-full min-h-[44px] mt-4 rounded-xl bg-gradient-to-r from-[#f5faf0] via-[#edf6e8] to-[#f5faf0] hover:from-[#edf5e6] hover:to-[#e4efe0] transition-all px-4 py-3 flex items-center justify-between border-2 border-[#b5cca8] text-left shadow-sm hover:shadow ms-share-nudge-glow group cursor-pointer"
          >
            <span className="text-[12.5px] text-[#33462b] font-medium flex items-center gap-2">
              <span className="text-base">🌿</span>
              Know someone who could use five calm minutes?
            </span>
            <span className="ms-share-btn-attention text-[12px] font-bold text-white bg-[#0a331c] group-hover:bg-[#14281e] px-3 py-1.5 rounded-lg flex items-center gap-1.5 shrink-0 ml-3 shadow-md border border-[#7d9667]/50">
              Share <Share2 size={12} className="text-[#a8d392]" />
            </span>
          </button>

          {/* ── Referral program ─────────────────────────── */}
          <div className="rounded-2xl border border-[#e1ebd9] mt-4 p-4 sm:p-5 md:p-6">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-[#f0f6ea] flex items-center justify-center">
                <Gift size={14} className="text-[#7d9667]" />
              </span>
              <h3 className="ms-serif text-[1.2rem] text-[#1e3019]">Give a month, get a month</h3>
            </div>
            <p className="text-[12.5px] text-[#5c6f52] leading-relaxed mt-2">
              Every friend who joins with your link gets a free month of Premium — and so do you, up to three months.
            </p>

            {/* Reward track */}
            <div className="mt-5">
              <div className="relative h-1.5 rounded-full bg-[#e6efe0] overflow-hidden">
                <div
                  className="ms-track-fill absolute inset-y-0 left-0 rounded-full bg-[#7d9667]"
                  style={{ width: `${(referralCount / 3) * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-2">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="flex flex-col items-center gap-1 w-[60px]">
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        n <= referralCount
                          ? "bg-[#7d9667] text-white"
                          : "bg-white border border-[#cfe0c6] text-[#9db287]"
                      }`}
                    >
                      {n <= referralCount ? <Check size={11} /> : n}
                    </span>
                    <span className="text-[9.5px] text-[#8a8f7f]">Month {n}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Next reward focus card */}
            {nextRewardAt ? (
              <div className="mt-5 rounded-xl bg-[#f7faf5] border border-[#e6efe0] p-3 sm:p-4">
                <p className="text-[12px] font-semibold text-[#22331b]">
                  Invite one more friend to unlock month {nextRewardAt}.
                </p>
                <div className="flex flex-col sm:flex-row items-stretch gap-2 mt-3">
                  <input
                    type="text"
                    readOnly
                    value={refLink}
                    onFocus={(e) => e.target.select()}
                    aria-label="Your referral link"
                    className="w-full min-w-0 min-h-[44px] sm:min-h-0 rounded-lg bg-white border border-[#dbe7d3] px-3 py-2 text-base sm:text-xs font-medium text-[#3a4d31] outline-none select-all"
                  />
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={copyReferralLink}
                      onMouseEnter={() => setPausedAutoClose(true)}
                      className="flex-1 sm:flex-none min-h-[44px] sm:min-h-[36px] px-3.5 py-2 rounded-lg bg-[#2e3b29] hover:bg-[#1f281b] text-white font-semibold text-xs transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap"
                    >
                      <Copy size={13} /> Copy
                    </button>
                    <button
                      type="button"
                      onClick={shareReferralLink}
                      onMouseEnter={() => setPausedAutoClose(true)}
                      className="ms-share-btn-attention relative flex-1 sm:flex-none min-h-[44px] sm:min-h-[36px] px-4 py-2 rounded-lg bg-gradient-to-r from-[#0a331c] to-[#1c472d] hover:from-[#062413] hover:to-[#133520] text-white font-bold text-xs transition-all duration-300 border-2 border-[#7d9667] flex items-center justify-center gap-1.5 whitespace-nowrap shadow-md cursor-pointer"
                    >
                      <span className="relative flex h-2 w-2 mr-0.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#b8e2a2] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#b8e2a2]"></span>
                      </span>
                      <Share2 size={13} className="text-[#cde8ba]" />
                      <span>Share</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-xl bg-[#f7faf5] border border-[#e6efe0] p-4 flex items-center gap-2.5">
                <Sparkles size={16} className="text-[#7d9667] shrink-0" />
                <p className="text-[12.5px] font-semibold text-[#22331b]">
                  All three months unlocked — Premium's on us through the year.
                </p>
              </div>
            )}
            {!nextRewardAt && (
              <p className="text-[12.5px] text-[#5c6f52] text-center mt-3 leading-relaxed">
                If you enjoy using <strong>Mental Swasthya</strong>, please share it with your friends and family.
              </p>
            )}
          </div>

          <p
            role="status"
            aria-live="polite"
            className="text-xs text-[#7d9667] text-center mt-4 min-h-[1rem]"
          >
            {localApiMessage || apiMessage}
          </p>

          {/* ── Actions ───────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              type="button"
              onClick={handleDone}
              className="flex-1 min-h-[44px] rounded-xl bg-[#7d9667] hover:bg-[#6f865c] text-white px-5 py-3 text-sm font-semibold shadow-sm transition-colors"
            >
              Return to dashboard
            </button>
            <button
              type="button"
              onClick={handleTomorrow}
              className="flex-1 min-h-[44px] rounded-xl border border-[#e1ebd9] hover:bg-[#f7faf5] text-[#5c6f52] px-5 py-3 text-sm font-semibold transition-colors"
            >
              I'll be back tomorrow
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
